import { LoadingArea } from '@consigli/facade';
import { useGetBlobsQuery, usePackage, usePackageId, useProjectId } from '@consigli/hooks';
import { Document } from '@consigli/types';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HiDocumentText } from 'react-icons/hi';

import {
  CardBasedFileDisplay,
  LUMINAIRE_INPUT_SECTIONS,
} from '@/components/card-based-file-display';
import { WaitingForAnalysis } from '@/components/waiting-for-analysis';
import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';
import { useDemoMode } from '@/util/demo-mode';
import { parseBlobResults } from '@/util/file';

export const LuminaireScheduleDocumentsPage: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { adminFeaturesEnabled } = useDemoMode();

  const { pkg } = usePackage(projectId, packageId);
  const isPublished = pkg ? pkg.published : false;

  const {
    data: blobs,
    isLoading,
    isFetching,
    isError,
  } = useGetBlobsQuery(
    { projectId: projectId!, packageId: packageId!, page: 'all' },
    { skip: !projectId || !packageId },
  );

  const documents: Document[] = useMemo(() => {
    if (!blobs?.results) return [];
    return parseBlobResults(blobs.results);
  }, [blobs]);

  const waitingForAnalysis = useMemo(() => {
    if (adminFeaturesEnabled) return false;
    return !isPublished;
  }, [isPublished, adminFeaturesEnabled]);

  if (!packageId) {
    return (
      <WorkspaceProvider>
        <WorkspaceLayout hideTabs={true}>
          <Breadcrumb label={t('luminaire-schedule-tabs.documents')} />
          <LoadingArea title={t('document-list.loading')} />
        </WorkspaceLayout>
      </WorkspaceProvider>
    );
  }

  return (
    <WorkspaceProvider>
      <WorkspaceLayout hideTabs={!packageId || waitingForAnalysis}>
        <Breadcrumb label={t('luminaire-schedule-tabs.documents')} />
        {waitingForAnalysis ? (
          <WaitingForAnalysis />
        ) : isLoading ? (
          <LoadingArea title={t('document-list.loading')} />
        ) : isError || documents.length === 0 ? (
          <div className="flex items-center justify-center w-full min-h-[calc(100vh-200px)]">
            <div className="text-center">
              <HiDocumentText className="mx-auto h-16 w-16 text-day-neutral-subtle mb-4" />
              <h3 className="text-xl font-medium text-day-neutral-subtle mb-2">
                {t('document-list.no-results')}
              </h3>
            </div>
          </div>
        ) : (
          <CardBasedFileDisplay
            files={documents}
            isFetching={isFetching}
            isPublished={isPublished}
            sections={LUMINAIRE_INPUT_SECTIONS}
          />
        )}
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
